import { useState, type ComponentType, type ReactNode } from "react";
import { IonHeader, IonToolbar, IonButtons, IonButton, IonTitle, IonContent, IonList, IonItem, IonPage, IonIcon } from "@ionic/react";
import { close, tvOutline } from "ionicons/icons";
import { useDeviceQuery } from "../query/deviceQuery";
import { useDeviceScreenTemplateMutation } from "../query/deviceScreenTemplateMutation";
import useUX from '../utils/ux';
import ModalFooter from '../components/ModalFooter';
import footerStyles from '../components/ModalFooter.module.css';
import { useCommonTranslation, useModalTranslation } from '../hooks/useTranslation';
import { SCREEN_TEMPLATES } from '../utils/screenTemplates';
import { LastEventPreview, ActivityBoardPreview, LatestRecentPreview, InsideOutsidePreview } from './DeviceScreenPreviews';
import styles from './DeviceScreenTemplateModal.module.css';

const TEMPLATE_PREVIEWS: Record<string, ComponentType> = {
    'last-event': LastEventPreview,
    activity: ActivityBoardPreview,
    'latest-recent': LatestRecentPreview,
    'inside-outside': InsideOutsidePreview,
};

// Selection is shown by the row tint and the preview's ring — no radio
// control; tapping anywhere in the row selects it.
const LayoutRow = ({ title, note, current, currentLabel, selected, preview, onSelect }: {
    title: string,
    note?: string,
    current: boolean,
    currentLabel: string,
    selected: boolean,
    preview?: ReactNode,
    onSelect: () => void,
}) => (
    <IonItem button detail={false} onClick={onSelect}
        aria-pressed={selected}
        className={selected ? styles.rowSelected : undefined}>
        <div className={styles.rowBody}>
            <h2 className={styles.rowTitle}>
                {title}
                {current && <span className={styles.currentPill}>{currentLabel}</span>}
            </h2>
            {note && <p className={styles.rowNote}>{note}</p>}
            {preview && (
                <div className={selected ? `${styles.preview} ${styles.previewSelected}` : styles.preview}>
                    {preview}
                </div>
            )}
        </div>
    </IonItem>
);

/** Picks the layout of the idle screen the device's e-paper panel shows. */
const DeviceScreenTemplateModal = ({ dismiss, deviceId }: { dismiss: (data?: string | null | undefined | number, role?: string) => void, deviceId: string }) => {
    const { t } = useModalTranslation();
    const { t: tCommon } = useCommonTranslation();
    const deviceQuery = useDeviceQuery(deviceId);
    const mutation = useDeviceScreenTemplateMutation(deviceId);
    const { presentResult } = useUX();

    // null = the fleet default (a device that never picked a layout).
    const currentTemplate: string | null = deviceQuery.data?.screenTemplate ?? null;
    // A device can be pinned to a template this app version doesn't list;
    // keep it selectable so the current state stays visible (cf. the custom
    // firmware channel row).
    const unknownTemplate = currentTemplate && !SCREEN_TEMPLATES.some((template) => template.id === currentTemplate)
        ? currentTemplate : null;
    const currentLabel = t('device_screen.current_label');
    // Explicit Default row: picking it saves null. Clearing a device's
    // template is what lets it fall back to the firmware's built-in ready
    // screen (the render service retracts the board when the fleet default
    // resolves to no screen), so the row must stay selectable.
    const DEFAULT_ROW = '__default__';
    const [selected, setSelected] = useState<string | null>(currentTemplate ?? DEFAULT_ROW);
    const selectedTemplate = selected === DEFAULT_ROW ? null : selected;

    const save = () => {
        presentResult(async () => {
            await mutation.mutateAsync(selectedTemplate);
            dismiss(selectedTemplate, 'select');
        }, t('device_screen.updated'));
    };

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar>
                    <IonTitle>{t('device_screen.title')}</IonTitle>
                    <IonButtons slot="primary">
                        <IonButton onClick={() => dismiss(null, 'cancel')}>
                            <IonIcon slot="icon-only" icon={close} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent color="light">
                <div className={styles.wrapper}>
                    <div className={styles.introCard}>
                        <span className={styles.introIcon} aria-hidden="true">
                            <IonIcon icon={tvOutline} />
                        </span>
                        <div className={styles.introBody}>
                            <h2 className={styles.introTitle}>{t('device_screen.intro_title')}</h2>
                            <p className={styles.introText}>{t('device_screen.intro_text')}</p>
                        </div>
                    </div>

                    <section className={styles.section}>
                        <h3 className={styles.sectionLabel}>{t('device_screen.section_layout')}</h3>
                        <IonList className={styles.listCard}>
                            <LayoutRow
                                title={t('device_screen.default_label')}
                                note={t('device_screen.default_note')}
                                current={currentTemplate === null}
                                currentLabel={currentLabel}
                                selected={selected === DEFAULT_ROW}
                                onSelect={() => setSelected(DEFAULT_ROW)} />
                            {SCREEN_TEMPLATES.map((template) => {
                                const Preview = TEMPLATE_PREVIEWS[template.id];
                                return (
                                    <LayoutRow key={template.id}
                                        title={tCommon(template.labelKey)}
                                        note={t(template.noteKey)}
                                        current={currentTemplate === template.id}
                                        currentLabel={currentLabel}
                                        selected={selected === template.id}
                                        preview={Preview && <Preview />}
                                        onSelect={() => setSelected(template.id)} />
                                );
                            })}
                            {unknownTemplate && (
                                <LayoutRow
                                    title={unknownTemplate}
                                    current
                                    currentLabel={currentLabel}
                                    selected={selected === unknownTemplate}
                                    onSelect={() => setSelected(unknownTemplate)} />
                            )}
                        </IonList>
                        <p className={styles.note}>{t('device_screen.note')}</p>
                    </section>
                </div>
            </IonContent>

            <ModalFooter>
                <IonButton
                    expand="block"
                    className={footerStyles.primaryButton}
                    onClick={save}
                    disabled={selectedTemplate === currentTemplate || mutation.isPending}
                >
                    {tCommon('buttons.save')}
                </IonButton>
            </ModalFooter>
        </IonPage>
    );
};

export default DeviceScreenTemplateModal;
